/**
 * Admin user management (§9).
 *
 * Mirrors DragDuro's User Oversight in structure — a filterable list where each
 * row carries both identity and the operative action, admins visibly
 * `protected` rather than actionable — and diverges where this app's API is
 * genuinely different: search is server-side (`GET /admin/users?q=`), there is
 * real pagination behind `limit`/`offset`, and deletion exists at all.
 *
 * The rule that matters most, taken straight from DragDuro: **an admin cannot
 * be acted on.** The API enforces it (`assertTargetNotAdmin`, checked against a
 * live email allowlist, not a cached flag), and the UI mirrors that by showing
 * `protected` where the buttons would be. The label is a mirror, never the
 * enforcement.
 */

import { useEffect, useState } from 'react';
import { Search, ShieldCheck, Trash2, UserX } from 'lucide-react';

import type { AdminUserRow } from '@svcdue/contracts';

import { useAdminUser, useAdminUsers, useDeleteUser, useSetUserDisabled } from '../api/hooks';
import { Empty, ErrorState, Loading, errorMessage } from '../components/states';

const PAGE_SIZE = 25;

/**
 * Search hits the API, so it is debounced — DragDuro filters an in-memory array
 * and needs no delay, but a keystroke-per-request against a real endpoint would
 * be both wasteful and racy. 250ms is below the threshold where typing feels
 * laggy and well above a fast typist's inter-key gap.
 */
function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

export function AdminUsers() {
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const debouncedSearch = useDebounced(search, 250);
  const [selected, setSelected] = useState<string | null>(null);

  // A new search must start at page one — otherwise a query with three matches
  // viewed at offset 50 renders an empty list that looks like a broken filter.
  useEffect(() => setOffset(0), [debouncedSearch]);

  const query = useAdminUsers({ q: debouncedSearch || undefined, limit: PAGE_SIZE, offset }, true);

  return (
    <div className="admin-panel">
      <div className="admin-h">
        <div>
          <h2 className="h2">Users</h2>
          <span className="admin-sub">{query.data ? `${query.data.total} total` : 'Loading…'}</span>
        </div>
        <div className="admin-search">
          <Search size={13} />
          <input
            className="input input-sm"
            value={search}
            placeholder="Filter by name or email…"
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search users"
          />
        </div>
      </div>

      {query.isPending && <Loading label="Loading users…" />}
      {query.isError && <ErrorState error={query.error} onRetry={() => void query.refetch()} />}

      {query.data && query.data.users.length === 0 && (
        <Empty
          title={debouncedSearch ? 'No users match that search' : 'No users yet'}
          hint={debouncedSearch ? 'Try a different name or email.' : undefined}
        />
      )}

      {query.data && query.data.users.length > 0 && (
        <>
          <div className="user-list">
            {query.data.users.map((user) => (
              <UserRow
                key={user.id}
                user={user}
                expanded={selected === user.id}
                onToggle={() => setSelected(selected === user.id ? null : user.id)}
              />
            ))}
          </div>

          <Pager
            offset={offset}
            total={query.data.total}
            count={query.data.users.length}
            onOffset={setOffset}
          />
        </>
      )}
    </div>
  );
}

function Pager({
  offset,
  total,
  count,
  onOffset,
}: {
  offset: number;
  total: number;
  count: number;
  onOffset: (offset: number) => void;
}) {
  if (total <= PAGE_SIZE) return null;

  return (
    <div className="pager">
      <button className="act act-ghost" disabled={offset === 0} onClick={() => onOffset(Math.max(0, offset - PAGE_SIZE))}>
        Previous
      </button>
      <span className="pager-at">
        {offset + 1}–{offset + count} of {total}
      </span>
      <button className="act act-ghost" disabled={offset + count >= total} onClick={() => onOffset(offset + PAGE_SIZE)}>
        Next
      </button>
    </div>
  );
}

function UserRow({ user, expanded, onToggle }: { user: AdminUserRow; expanded: boolean; onToggle: () => void }) {
  const setDisabled = useSetUserDisabled();
  const [deleting, setDeleting] = useState(false);

  return (
    <div className={'user-row' + (user.disabledAt ? ' user-disabled' : '')}>
      <button className="user-main" onClick={onToggle} aria-expanded={expanded}>
        <span className="user-ic">{user.name.slice(0, 1).toUpperCase()}</span>
        <span className="user-id">
          <b>
            {user.name || '(no name)'}
            {user.isAdmin && (
              <span className="user-admin">
                <ShieldCheck size={10} /> admin
              </span>
            )}
            {user.disabledAt && <span className="user-off">disabled</span>}
          </b>
          <span>{user.email}</span>
        </span>
        <span className="user-counts">
          <span>
            <b>{user.garageCount}</b> garages
          </span>
          <span>
            <b>{user.carCount}</b> cars
          </span>
        </span>
      </button>

      <div className="user-ops">
        {/**
         * Admins get a label, not buttons. The API refuses to disable or delete
         * one — including the operator's own account, which is the whole point:
         * the undo for a mistake here lives behind this same panel.
         */}
        {user.isAdmin ? (
          <span className="user-protected" title="Admin accounts cannot be disabled or deleted">
            protected
          </span>
        ) : (
          <>
            <button
              className="act act-ghost"
              disabled={setDisabled.isPending}
              onClick={() => setDisabled.mutate({ userId: user.id, disabled: !user.disabledAt })}
            >
              <UserX size={12} /> {user.disabledAt ? 'Enable' : 'Disable'}
            </button>
            <button className="act act-danger" onClick={() => setDeleting(true)}>
              <Trash2 size={12} />
            </button>
          </>
        )}
      </div>

      {setDisabled.isError && <div className="field-err">{errorMessage(setDisabled.error)}</div>}
      {expanded && <UserDetail userId={user.id} />}
      {deleting && <DeleteUserConfirm user={user} onClose={() => setDeleting(false)} />}
    </div>
  );
}

function UserDetail({ userId }: { userId: string }) {
  const query = useAdminUser(userId);

  if (query.isPending) return <Loading label="Loading memberships…" />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  if (!query.data) return null;

  const { memberships, user } = query.data;

  return (
    <div className="user-detail">
      <div className="user-detail-meta">
        <span>
          Joined <b>{new Date(user.createdAt).toLocaleDateString('en-US')}</b>
        </span>
        {user.disabledAt && (
          <span>
            Disabled <b>{new Date(user.disabledAt).toLocaleDateString('en-US')}</b>
          </span>
        )}
      </div>

      {memberships.length === 0 ? (
        <div className="user-detail-empty">In no garages.</div>
      ) : (
        <div className="membership-list">
          {memberships.map((m) => (
            <div className="membership" key={m.garageId}>
              <b>{m.garageName}</b>
              <span className={'gsw-role role-' + m.role}>{m.role}</span>
              {m.isPublic && <span className="membership-public">public</span>}
              <span className="membership-cars">{m.carCount} cars</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Delete an account, confirmed by typing its email.
 *
 * The typed confirmation is the API's requirement, not a UI flourish — the route
 * takes `confirmEmail` and rejects a mismatch (routes/admin.ts). Mirroring it
 * here means the button cannot even be pressed until the strings match, so the
 * server-side check is a backstop rather than the first line of defence.
 *
 * The comparison is lowercased on both sides because the contract lowercases
 * the field (`deleteUserRequest`); a case-sensitive check in the UI would
 * disable the button for input the API would happily have accepted.
 */
function DeleteUserConfirm({ user, onClose }: { user: AdminUserRow; onClose: () => void }) {
  const [typed, setTyped] = useState('');
  const remove = useDeleteUser();

  const matches = typed.trim().toLowerCase() === user.email.toLowerCase();

  return (
    <div className="danger-zone">
      <div className="danger-h">
        <Trash2 size={13} /> Delete {user.email}
      </div>
      <p className="danger-p">
        This is permanent. It deletes the account and <b>every garage it owns</b> — the cars, parts, events and service
        history in them. Garages this user only crews are untouched.
      </p>
      <p className="danger-p danger-ask">
        Type <b>{user.email}</b> to confirm.
      </p>
      <input
        className="input"
        value={typed}
        autoFocus
        placeholder={user.email}
        onChange={(e) => setTyped(e.target.value)}
        aria-label="Type the email to confirm deletion"
      />

      {remove.isError && <div className="field-err">{errorMessage(remove.error)}</div>}
      {remove.isSuccess && (
        <div className="danger-done">
          Deleted {remove.data.deleted.garages} garages, {remove.data.deleted.cars} cars, {remove.data.deleted.items}{' '}
          parts, {remove.data.deleted.events} events.
        </div>
      )}

      <div className="danger-acts">
        <button className="act act-ghost" onClick={onClose}>
          Cancel
        </button>
        <button
          className="act act-danger"
          disabled={!matches || remove.isPending}
          onClick={() => remove.mutate({ userId: user.id, body: { confirmEmail: user.email.toLowerCase() } })}
        >
          {remove.isPending ? 'Deleting…' : 'Delete permanently'}
        </button>
      </div>
    </div>
  );
}
